import type { EventBus } from '@hc-frontend/core-events';
import type { SubmitFormEvent } from '@hc-frontend/core-ui';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import type { Optional } from '@hc-frontend/core-utils-types';
import {
  type FormElement,
  normalizeAndHash,
} from '@hc-frontend/third-party-snowplow';
import type { SelfDescribingJson } from '@snowplow/browser-tracker';

import type { SubmitPersonalInformationPlateTrackingOptions } from '../../domain/entities/submit-personal-information-plate-tracking';
import type { SubmitPersonalInformationPlateTrackingRepository } from '../../domain/repositories/submit-personal-information-plate-tracking-repository';

export interface TrackSubmitPersonalInformationPlateAdapterFactoryOptions {
  eventBus: EventBus;
  trackingContexts: SelfDescribingJson[];
}

function normalizeAndHashOrNull(value: Optional<string>) {
  return value ? normalizeAndHash(value) : null;
}

const buildFormElements = ({
  firstName,
  lastName,
  email,
  streetAddress,
  phoneNumber,
  smsConsent,
}: SubmitPersonalInformationPlateTrackingOptions) => {
  const firstNameElement: FormElement = {
    name: 'FIRST_NAME',
    value: normalizeAndHashOrNull(firstName),
    nodeName: 'INPUT',
  };
  const lastNameElement: FormElement = {
    name: 'LAST_NAME',
    value: normalizeAndHashOrNull(lastName),
    nodeName: 'INPUT',
  };
  const emailElement: FormElement = {
    name: 'EMAIL',
    value: normalizeAndHashOrNull(email),
    nodeName: 'INPUT',
  };
  const streetAddressElement: FormElement = {
    name: 'STREET_ADDRESS',
    value: normalizeAndHashOrNull(streetAddress),
    nodeName: 'INPUT',
  };
  const phoneNumberElement: FormElement = {
    name: 'PHONE_NUMBER',
    value: normalizeAndHashOrNull(phoneNumber),
    nodeName: 'INPUT',
  };
  const smsConsentElement: FormElement = {
    name: 'SMS_CONSENT',
    value: smsConsent ?? null,
    nodeName: 'INPUT',
  };
  return [
    firstNameElement,
    lastNameElement,
    emailElement,
    streetAddressElement,
    phoneNumberElement,
    smsConsentElement,
  ];
};

export function trackSubmitPersonalInformationPlateAdapterFactory({
  eventBus,
  trackingContexts,
}: TrackSubmitPersonalInformationPlateAdapterFactoryOptions): Pick<
  SubmitPersonalInformationPlateTrackingRepository,
  'trackSubmitPersonalInformationPlate'
> {
  return {
    trackSubmitPersonalInformationPlate: async (options) => {
      try {
        const formElements = buildFormElements(options);
        const formId = options.plateName;

        eventBus.publish<
          SubmitFormEvent,
          (EventTarget & HTMLFormElement) | null
        >({
          id: formId,
          topic: 'core.ui.form.submit',
          source: null,
          submitForm: {
            id: formId,
            elements: formElements,
          },
          contexts: trackingContexts,
        });

        return createSuccessOperationResult(undefined);
      } catch (error) {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failedToTrackSubmitPersonalInformationPlate',
              rootCause: error as Error,
            },
          ],
        });
      }
    },
  };
}
